/**
 * TexasPricingSection (F-285, us2 lane)
 *
 * The Texas pricing surface: needs-assessment quiz → recommended tier with a
 * personalized price (mirrors the UK AssessmentWizard flow), per-use services
 * priced through the gleaning margin, and FastSpring popup checkout against
 * the us2 storefront.
 *
 * Human directive 2026-09-28: the four published tier cards are REMOVED from
 * this section (they read as UK-style price boxes on the Texas page) — pricing
 * is surfaced through the quiz recommendation only.
 *
 * Kingdom-business pricing principles, surfaced in the UI:
 *  - Just weights (Proverbs 11:1): one flat published price — no per-unit creep.
 *  - Gleaning margin (Lev 19:9-10): per-use services cost less for small
 *    portfolios — the edge of the field left for the gleaner.
 *  - First fruits (Exodus 23:19): a 14-day full-access trial; pay after the
 *    platform has proven itself, not before.
 *
 * FastSpring wiring (AC 1-3):
 *  - Storefront from REACT_APP_FASTSPRING_STORE_ID via fastspringStorefront.ts
 *    (fail-closed; no hardcode). UK builds never load it (market gate).
 *  - Popup checkout: POST api/usd-catalog/checkout → builder.push(checkout).
 *  - Success → /payment-success; popup closed without order → retry prompt;
 *    SBL/checkout errors → inline retry path.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { landlordApi } from '../../services/api';
import MeetingLinkCta from '../Shared/MeetingLinkCta';
import { readCachedTier } from '../../services/subscriptionEntitlement';
import { trackServicePurchased, trackUpgraded } from '../../services/analytics';
import { useUsdPricing } from '../../hooks/useUsdPricing';
import {
  FIRST_FRUITS_TRIAL_DAYS,
  QuizAnswers,
  QuizRecommendation,
  UsdPlan,
  formatUsd,
  gleaningDiscountPct,
  gleaningPrice,
  maxUnitsOf,
  recommendTier,
} from '../../services/usdPricingModel';
import {
  attachPopupSession,
  detachPopupSession,
  loadUs2Storefront,
  openPopupSession,
  resolveStorefront,
} from '../../services/fastspringStorefront';

const QUIZ_QUESTIONS: { id: keyof QuizAnswers; question: string; options: { label: string; value: string; icon: string }[] }[] = [
  {
    id: 'units',
    question: 'How many units do you manage?',
    options: [
      { label: '1 unit', value: '1', icon: 'fa-home' },
      { label: '2–5 units', value: '2-5', icon: 'fa-building' },
      { label: '6–20 units', value: '6-20', icon: 'fa-city' },
      { label: '21+ units', value: '21+', icon: 'fa-building-columns' },
    ],
  },
  {
    id: 'services',
    question: 'What do you need most?',
    options: [
      { label: 'Compliance tracking', value: 'compliance', icon: 'fa-clipboard-check' },
      { label: 'Tenant management', value: 'tenant', icon: 'fa-users' },
      { label: 'Legal documents + evictions', value: 'legal', icon: 'fa-gavel' },
      { label: 'All of the above', value: 'all', icon: 'fa-layer-group' },
    ],
  },
  {
    id: 'evictions',
    question: 'How often do you file evictions?',
    options: [
      { label: 'Rarely or never', value: 'none', icon: 'fa-circle-check' },
      { label: 'A few per year', value: 'sometimes', icon: 'fa-scale-balanced' },
      { label: 'Regularly', value: 'frequent', icon: 'fa-repeat' },
    ],
  },
];

type CheckoutState = 'idle' | 'starting' | 'popup' | 'success' | 'retry' | 'error';

interface CheckoutMeta {
  message: string;
  tierId?: string;
}

const TexasPricingSection: React.FC = () => {
  const navigate = useNavigate();
  const { plans } = useUsdPricing();

  // Needs-assessment quiz state (null = quiz hidden, cards shown first).
  const [quizStep, setQuizStep] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>({});
  const [recommendation, setRecommendation] = useState<QuizRecommendation | null>(null);

  // FastSpring checkout state machine (AC 2-3).
  const [checkout, setCheckout] = useState<CheckoutState>('idle');
  const [checkoutMeta, setCheckoutMeta] = useState<CheckoutMeta | null>(null);
  const pendingRef = useRef<string | null>(null);

  const storeReady = resolveStorefront().ok;

  const trackEvent = (eventName: string, params?: Record<string, string>) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params);
    }
  };

  // ── Quiz flow ───────────────────────────────────────────────────────────────

  const startQuiz = () => {
    trackEvent('pricing_quiz_start', { market: 'texas' });
    setAnswers({});
    setRecommendation(null);
    setQuizStep(0);
  };

  const answerQuestion = (value: string) => {
    if (quizStep === null) return;
    const current = QUIZ_QUESTIONS[quizStep];
    const next = { ...answers, [current.id]: value };
    setAnswers(next);
    if (quizStep < QUIZ_QUESTIONS.length - 1) {
      setQuizStep(quizStep + 1);
      return;
    }
    const rec = recommendTier(next as QuizAnswers);
    setRecommendation(rec);
    setQuizStep(null);
    trackEvent('pricing_quiz_complete', { market: 'texas', tier: rec.tierId });
  };

  // ── FastSpring checkout (AC 1-3) ────────────────────────────────────────────

  const startCheckout = useCallback(
    async (tierId: string, tierName: string) => {
      const token = localStorage.getItem('idToken');
      if (!token) {
        navigate('/login?redirect=/');
        return;
      }
      if (!storeReady) {
        const resolution = resolveStorefront();
        setCheckout('error');
        setCheckoutMeta({
          message: resolution.ok ? '' : resolution.message,
          tierId,
        });
        return;
      }

      setCheckout('starting');
      setCheckoutMeta({ message: '', tierId });
      pendingRef.current = tierId;

      try {
        // Load the SBL for the us2 storefront before opening the popup.
        await loadUs2Storefront();

        // US-146 contract ruling (dispatch 233b419): LP usd-catalog.js has
        // NO checkout endpoint — the old POST api/usd-catalog/checkout here
        // always 404'd. The popup only needs a product path + storefront,
        // so the payload is built CLIENT-SIDE from the F-284 catalog:
        // the tier subscription SKU is the live payload match when present,
        // else the canonical F-series path 'landlord-pro-<tierId>' (the
        // shape the F-195 webhook bridge resolves back to the tier).
        // NO LP route was added or patched (dispatch forbids LP changes).
        let path: string | null = null;
        try {
          const res = await landlordApi.get('api/usd-catalog/products');
          const data = res?.data?.data ?? res?.data ?? {};
          const products: any[] = Array.isArray(data.products) ? data.products : [];
          const hit = products.find(
            (p) => p && p.kind === 'subscription' && String(p.tierId) === String(tierId) && p.productPath
          );
          if (hit) path = String(hit.productPath);
        } catch {
          /* catalog unreachable → canonical path fallback */
        }
        if (!path) path = `landlord-pro-${tierId}`;
        const popupData = {
          products: [{ path, quantity: 1 }],
          tags: { tier: tierId },
          contact: {},
        };

        attachPopupSession({
          onClosed: (orderReference: any) => {
            detachPopupSession();
            if (orderReference?.id) {
              setCheckout('success');
              setCheckoutMeta({ message: 'Payment received — welcome aboard! Setting up your plan…', tierId });
              trackEvent('checkout_complete', { market: 'texas', tier: tierId });
              // Batch 4 S6: core funnel events (consent-gated).
              const prevTier = readCachedTier();
              trackServicePurchased({ tier: tierId, channel: 'fastspring' });
              if (prevTier && prevTier !== tierId) {
                trackUpgraded({ from: prevTier, to: tierId, channel: 'fastspring' });
              }
              setTimeout(() => navigate(`/payment-success?product=${tierId}`), 400);
            } else {
              // Popup closed without a completed order → retry prompt (AC 3).
              setCheckout('retry');
              setCheckoutMeta({ message: 'Checkout closed before completion — your selection is still ready.', tierId });
            }
            setCheckoutStateResetTimer();
          },
          onData: () => {
            /* order lifecycle detail; webhook (F-195) is authoritative */
          },
          onError: (code: string, message: string) => {
            detachPopupSession();
            setCheckout('error');
            setCheckoutMeta({ message: message || `Payment error (${code})`, tierId });
            trackEvent('checkout_error', { market: 'texas', tier: tierId, code });
          },
        });

        openPopupSession(popupData);
        setCheckout('popup');
      } catch (err: any) {
        setCheckout('error');
        setCheckoutMeta({
          message: err?.response?.data?.error || err?.message || 'Failed to start checkout.',
          tierId,
        });
        trackEvent('checkout_error', { market: 'texas', tier: tierId, code: 'start_failed' });
      }
    },
    [navigate, storeReady]
  );

  const setCheckoutStateResetTimer = () => {
    window.setTimeout(() => {
      setCheckout((prev) => (prev === 'retry' || prev === 'success' ? 'idle' : prev));
    }, 8000);
  };

  const retryCheckout = () => {
    const tierId = checkoutMeta?.tierId || pendingRef.current;
    setCheckout('idle');
    setCheckoutMeta(null);
    if (tierId) {
      const tier = plans.find((p) => p.id === tierId);
      startCheckout(tierId, tier?.name || tierId);
    }
  };

  useEffect(() => () => detachPopupSession(), []);

  // ── Render helpers ──────────────────────────────────────────────────────────

  const priceLabel = (plan: UsdPlan): string => {
    if (plan.contactSales) return 'Custom';
    if (plan.price === 0) return 'Free';
    return `${formatUsd(plan.price)}/mo`;
  };

  const gleaningUnits = answers.units ? maxUnitsOf(answers.units) : null;
  const gleaningPct = gleaningUnits !== null ? gleaningDiscountPct(gleaningUnits) : null;

  const busy = checkout === 'starting';

  // ── Quiz renderer ───────────────────────────────────────────────────────────

  if (quizStep !== null) {
    const current = QUIZ_QUESTIONS[quizStep];
    return (
      <section id="pricing" className="py-16 px-4 bg-white">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8 border border-slate-100">
          <div className="flex items-center justify-between mb-6">
            <span className="text-sm font-medium text-emerald-600">
              Question {quizStep + 1} of {QUIZ_QUESTIONS.length}
            </span>
            <div className="flex gap-1">
              {QUIZ_QUESTIONS.map((_, i) => (
                <div
                  key={i}
                  className={`h-2 rounded-full ${i <= quizStep ? 'bg-emerald-600 w-8' : 'bg-slate-200 w-4'} transition-all`}
                />
              ))}
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">{current.question}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {current.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => answerQuestion(opt.value)}
                className="flex items-center gap-3 p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-600 hover:bg-emerald-50 transition text-left"
              >
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`fas ${opt.icon} text-emerald-600`} />
                </div>
                <span className="font-medium text-slate-900">{opt.label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setQuizStep(null)} className="mt-6 text-sm text-slate-500 hover:text-slate-700">
            ← Back to plans
          </button>
        </div>
      </section>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <section id="pricing" className="py-16 px-4 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <header className="text-center mb-10">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">Fair, flat, Texas-honest pricing</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            One flat price per tier — no per-unit creep. Start with a {FIRST_FRUITS_TRIAL_DAYS}-day full-access
            trial and only pay once it has earned its keep.
          </p>
          <button
            onClick={startQuiz}
            className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition"
          >
            <i className="fas fa-compass" /> Find my price in 3 questions
          </button>
        </header>

        {/* Recommendation card (post-quiz) */}
        {recommendation && (
          <div className="max-w-2xl mx-auto mb-10 bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              We recommend: {recommendation.tierName}
            </h3>
            <p className="text-slate-600 mb-4">{recommendation.reason}</p>
            {recommendation.monthlyUsd !== null ? (
              <div className="text-4xl font-extrabold text-emerald-700 mb-1">
                {formatUsd(recommendation.monthlyUsd)}
                <span className="text-base font-medium text-slate-500">/month</span>
              </div>
            ) : (
              <div className="text-2xl font-bold text-slate-800 mb-1">Custom pricing</div>
            )}
            <p className="text-sm text-slate-500 mb-6">
              After your {FIRST_FRUITS_TRIAL_DAYS}-day first-fruits trial.
              {recommendation.gleaningPct > 0 &&
                ` Per-use services at a ${recommendation.gleaningPct}% gleaning discount.`}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              {recommendation.offerId === 'first-fruits-15' ? (
                <button
                  onClick={() => startCheckout(recommendation.tierId, recommendation.tierName)}
                  disabled={busy}
                  className="bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition disabled:opacity-60"
                >
                  {busy ? 'Preparing checkout…' : recommendation.cta}
                </button>
              ) : (
                <a
                  href="mailto:sales@compliance-engine.io?subject=Texas%20Enterprise%20pricing"
                  className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 transition"
                >
                  {recommendation.cta}
                </a>
              )}
              <button
                onClick={() => setRecommendation(null)}
                className="bg-white text-slate-700 border border-slate-300 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition"
              >
                See all plans
              </button>
              {/* F-188 / T-165-002: meeting-link CTA (commitment-plan surface) */}
              <MeetingLinkCta
                tier={recommendation.tierId}
                campaign="texas-quiz"
                label="Book a call instead"
                className="bg-white text-slate-700 border border-slate-300 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition"
              />
            </div>

            {/* Personalized per-use strip through the gleaning margin */}
            {recommendation.perUse.length > 0 && (
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2 text-left">
                {recommendation.perUse.map((s) => (
                  <div key={s.type} className="bg-white rounded-lg border border-emerald-100 p-3">
                    <div className="text-xs text-slate-500">{s.name}</div>
                    <div className="font-bold text-slate-900">
                      {formatUsd(s.price)}
                      {s.price < s.base && (
                        <span className="ml-1 text-xs font-normal text-slate-400 line-through">{formatUsd(s.base)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Checkout status banner: retry / error / success (AC 3) */}
        {(checkout === 'retry' || checkout === 'error' || checkout === 'success') && (
          <div
            role="alert"
            className={`max-w-2xl mx-auto mb-8 rounded-xl border-2 p-4 text-center ${
              checkout === 'success'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                : 'bg-amber-50 border-amber-300 text-amber-900'
            }`}
          >
            <p className="font-medium">{checkoutMeta?.message}</p>
            {(checkout === 'retry' || checkout === 'error') && (
              <button
                onClick={retryCheckout}
                disabled={busy}
                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-lg font-semibold transition disabled:opacity-60"
              >
                {busy ? 'Preparing…' : 'Retry checkout'}
              </button>
            )}
          </div>
        )}

        {/* Gleaning strip: per-use services with portfolio-sized discounts */}
        {gleaningPct !== null && gleaningPct > 0 && (
          <div className="mt-10 text-center text-sm text-slate-600">
            <i className="fas fa-wheat-awn text-emerald-600 mr-1" />
            Gleaning margin: portfolios of 20 units or fewer pay{' '}
            <strong>{gleaningPct}% less</strong> on per-use services — the edge of the field
            is left for the small operator (Leviticus 19:9-10).
          </div>
        )}
      </div>
    </section>
  );
};

export default TexasPricingSection;
